package com.nonopn.skydome.common.app.pages.probes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.navigation.RouterProbeCreate
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.cancel
import com.nonopn.skywatcher.common.app.res.ok
import com.nonopn.skywatcher.common.app.res.probe_action_upgrade
import com.nonopn.skywatcher.common.app.res.probe_add_card
import com.nonopn.skywatcher.common.app.res.probe_demo_exists_body
import com.nonopn.skywatcher.common.app.res.probe_demo_exists_pay
import com.nonopn.skywatcher.common.app.res.probe_nothing_to_pay_body
import com.nonopn.skywatcher.common.app.res.probe_nothing_to_pay_title
import com.nonopn.skywatcher.common.app.res.probe_paid_subscription_body
import com.nonopn.skywatcher.common.app.res.probe_paid_subscription_title
import com.nonopn.skywatcher.common.app.res.probe_demo_exists_title
import com.nonopn.skywatcher.common.app.res.probe_err_account_has_probes_title
import com.nonopn.skywatcher.common.app.res.probe_err_account_has_probes_body
import com.nonopn.skywatcher.common.app.res.probe_err_pending_payment_title
import com.nonopn.skywatcher.common.app.res.probe_err_pending_payment_body
import com.nonopn.skywatcher.common.app.res.probe_err_no_demo_title
import com.nonopn.skywatcher.common.app.res.probe_err_no_demo_body
import com.nonopn.skywatcher.common.app.res.probe_err_invalid_position_title
import com.nonopn.skywatcher.common.app.res.probe_err_invalid_position_body
import com.nonopn.skywatcher.common.app.res.probe_err_invalid_name_title
import com.nonopn.skywatcher.common.app.res.probe_err_invalid_name_body
import com.nonopn.skywatcher.common.app.res.probe_err_too_many_requests_title
import com.nonopn.skywatcher.common.app.res.probe_err_too_many_requests_body
import com.nonopn.skywatcher.common.app.res.probe_error_body
import com.nonopn.skywatcher.common.app.res.probe_error_title
import com.nonopn.skywatcher.common.app.res.probe_nsg_promo_body
import com.nonopn.skywatcher.common.app.res.probe_nsg_promo_cta
import com.nonopn.skywatcher.common.app.res.probe_nsg_promo_title
import com.nonopn.skywatcher.common.app.res.probe_pdf_blocked
import com.nonopn.skywatcher.common.app.res.probe_promo_b1
import com.nonopn.skywatcher.common.app.res.probe_promo_b2
import com.nonopn.skywatcher.common.app.res.probe_promo_b3
import com.nonopn.skywatcher.common.app.res.probe_promo_b4
import com.nonopn.skywatcher.common.app.res.probe_promo_create
import com.nonopn.skywatcher.common.app.res.probe_promo_title
import com.nonopn.skywatcher.common.app.res.probe_promo_trial
import com.nonopn.skywatcher.common.app.res.probe_promo_trial_note
import com.nonopn.skywatcher.common.app.res.probe_trial_banner_last_day
import com.nonopn.skywatcher.common.app.res.probe_trial_banner_week
import com.nonopn.skywatcher.common.app.res.probe_unavailable_body
import com.nonopn.skywatcher.common.app.res.probe_unavailable_title
import com.nonopn.skywatcher.design.theme.AppColor
import eu.codlab.compose.widgets.TextNormal
import korlibs.time.DateTime
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.resources.stringResource

// Shared UI pieces of the probe creation / trial feature: promo cards, trial banner, PDF lock, error dialog.

internal val ProbeOrange = Color(0xFFF28C28)

internal fun nowSecondsUtc(): Long = DateTime.now().unixMillisLong / 1000L

/** The new buttons are hidden for regular accounts until the backend is ready (see [ProbeCreationFlag]). */
@Composable
fun rememberProbeCreationVisible(appModel: AppModel): Boolean {
    val state by appModel.states.collectAsState()
    return ProbeCreationFlag.isVisible(isAdmin = state.apiProvider?.apiAdmin != null)
}

/** Opens the probe creation screen, optionally in trial mode (a single probe). */
fun openProbeCreation(appModel: AppModel, trial: Boolean) {
    appModel.pendingProbeTrial = trial
    appModel.show(RouterProbeCreate.navigateTo())
}

/** Probe status of the account for one site: promo card and PDF lock. Loaded from the dashboard cache. */
@Composable
fun rememberProbeAccess(appModel: AppModel, latitude: Double, longitude: Double): ProbeAccess {
    var access by remember(latitude, longitude) { mutableStateOf(ProbeAccess()) }
    LaunchedEffect(latitude, longitude) {
        try {
            val probes = appModel.cachedDashboardReportsV2().mapNotNull { it.probe }.map {
                ProbeWithPosition(it.latitude, it.longitude, it.statusInput())
            }
            access = computeProbeAccess(probes, latitude, longitude, nowSecondsUtc())
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            // Best effort: on failure nothing is blocked and no promo is shown
        }
    }
    return access
}

@Composable
internal fun ProbePrimaryButton(text: String, color: Color, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(10.dp))
            .background(color)
            .clickable { onClick() }
            .padding(horizontal = 16.dp, vertical = 12.dp),
        contentAlignment = Alignment.Center
    ) {
        TextNormal(
            text,
            fontSize = 14.sp,
            fontWeight = FontWeight.Bold,
            color = Color.White,
            textAlign = TextAlign.Center
        )
    }
}

/** Dashboard promo card, shown while the account has no active or trial probe. */
@Composable
fun ProbePromoCard(
    onCreate: () -> Unit,
    onTrial: () -> Unit,
    modifier: Modifier = Modifier,
    // Each button only when the server offers it (AddProbes / StartDemo in account/status actions)
    showCreate: Boolean = true,
    showTrial: Boolean = true,
) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AppColor.StrikeCard)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        TextNormal(
            stringResource(Res.string.probe_promo_title),
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
            color = AppColor.StrikeText
        )
        listOf(
            Res.string.probe_promo_b1,
            Res.string.probe_promo_b2,
            Res.string.probe_promo_b3,
            Res.string.probe_promo_b4
        ).forEach {
            TextNormal("✓ " + stringResource(it), fontSize = 13.sp, color = AppColor.StrikeText.copy(alpha = 0.8f))
        }
        Spacer(Modifier.height(6.dp))
        if (showCreate) {
            ProbePrimaryButton(
                stringResource(Res.string.probe_promo_create),
                AppColor.StrikePrimary,
                onCreate,
                Modifier.fillMaxWidth()
            )
        }
        if (showTrial) {
            ProbePrimaryButton(
                stringResource(Res.string.probe_promo_trial),
                ProbeOrange,
                onTrial,
                Modifier.fillMaxWidth()
            )
            TextNormal(
                stringResource(Res.string.probe_promo_trial_note),
                fontSize = 11.sp,
                color = AppColor.StrikeText.copy(alpha = 0.55f),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

/** Nsg screen promo: an alternative to the per-unit purchase, never a replacement of it. */
@Composable
fun NsgProbePromo(onCreate: () -> Unit, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AppColor.StrikePrimary.copy(alpha = 0.10f))
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        TextNormal(
            "⚡ " + stringResource(Res.string.probe_nsg_promo_title),
            fontSize = 15.sp,
            fontWeight = FontWeight.Bold,
            color = AppColor.StrikePrimary
        )
        TextNormal(
            stringResource(Res.string.probe_nsg_promo_body),
            fontSize = 13.sp,
            color = AppColor.StrikeText.copy(alpha = 0.8f)
        )
        Spacer(Modifier.height(4.dp))
        ProbePrimaryButton(stringResource(Res.string.probe_nsg_promo_cta), AppColor.StrikePrimary, onCreate)
    }
}

/** "Add probes" card of the Probes page (opens the creation screen). */
@Composable
fun AddProbesCard(onClick: () -> Unit, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.5.dp, AppColor.StrikePrimary.copy(alpha = 0.35f), RoundedCornerShape(12.dp))
            .clickable { onClick() }
            .padding(vertical = 22.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        TextNormal("+", fontSize = 28.sp, fontWeight = FontWeight.Bold, color = AppColor.StrikePrimary)
        TextNormal(
            stringResource(Res.string.probe_add_card),
            fontSize = 13.sp,
            color = AppColor.StrikeText.copy(alpha = 0.65f)
        )
    }
}

/** In-app reminder at J-7 and J-1 of the end of the trial. */
@Composable
fun TrialBannerView(banner: TrialBanner, onUpgrade: (() -> Unit)?, modifier: Modifier = Modifier) {
    val text = when (banner.kind) {
        TrialBannerKind.Week -> stringResource(Res.string.probe_trial_banner_week, banner.daysLeft)
        TrialBannerKind.LastDay -> stringResource(Res.string.probe_trial_banner_last_day)
    }
    Column(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(ProbeOrange.copy(alpha = 0.15f))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        TextNormal(text, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = AppColor.StrikeText)
        onUpgrade?.let {
            ProbePrimaryButton(stringResource(Res.string.probe_action_upgrade), ProbeOrange, it)
        }
    }
}

/** Message shown instead of an enabled PDF export button while only trial probes cover the site. */
@Composable
fun PdfBlockedNotice(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(ProbeOrange.copy(alpha = 0.15f))
            .padding(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        TextNormal(
            "🔒 " + stringResource(Res.string.probe_pdf_blocked),
            fontSize = 12.sp,
            color = AppColor.StrikeText
        )
    }
}

/** Outcome dialog of a probe route: route unreachable, trial unavailable, nothing to pay, paid, or generic. */
data class ProbeCallError(val failure: ProbeFailure, val probeUuid: String? = null)

@Composable
fun ProbeFailureDialog(
    error: ProbeCallError,
    onDismiss: () -> Unit,
    onPayExisting: () -> Unit,
    // The "pay for my probe" button needs the Upgrade action (false when the status route says it is unavailable)
    canPayExisting: Boolean = true,
) {
    val demoExists = error.failure == ProbeFailure.DemoAlreadyExists && canPayExisting
    val title = stringResource(
        when (error.failure) {
            ProbeFailure.ServiceUnavailable -> Res.string.probe_unavailable_title
            ProbeFailure.DemoAlreadyExists -> Res.string.probe_demo_exists_title
            ProbeFailure.NothingToPay -> Res.string.probe_nothing_to_pay_title
            ProbeFailure.PaidBySubscription -> Res.string.probe_paid_subscription_title
            ProbeFailure.AccountHasProbes -> Res.string.probe_err_account_has_probes_title
            ProbeFailure.PendingPayment -> Res.string.probe_err_pending_payment_title
            ProbeFailure.NoDemo -> Res.string.probe_err_no_demo_title
            ProbeFailure.InvalidPosition -> Res.string.probe_err_invalid_position_title
            ProbeFailure.InvalidName -> Res.string.probe_err_invalid_name_title
            ProbeFailure.TooManyRequests -> Res.string.probe_err_too_many_requests_title
            ProbeFailure.Generic -> Res.string.probe_error_title
        }
    )
    val body = stringResource(
        when (error.failure) {
            ProbeFailure.ServiceUnavailable -> Res.string.probe_unavailable_body
            ProbeFailure.DemoAlreadyExists -> Res.string.probe_demo_exists_body
            ProbeFailure.NothingToPay -> Res.string.probe_nothing_to_pay_body
            ProbeFailure.PaidBySubscription -> Res.string.probe_paid_subscription_body
            ProbeFailure.AccountHasProbes -> Res.string.probe_err_account_has_probes_body
            ProbeFailure.PendingPayment -> Res.string.probe_err_pending_payment_body
            ProbeFailure.NoDemo -> Res.string.probe_err_no_demo_body
            ProbeFailure.InvalidPosition -> Res.string.probe_err_invalid_position_body
            ProbeFailure.InvalidName -> Res.string.probe_err_invalid_name_body
            ProbeFailure.TooManyRequests -> Res.string.probe_err_too_many_requests_body
            ProbeFailure.Generic -> Res.string.probe_error_body
        }
    )
    AlertDialog(
        onDismissRequest = onDismiss,
        title = { TextNormal(title, fontWeight = FontWeight.Bold) },
        text = { TextNormal(body) },
        confirmButton = {
            if (demoExists) {
                TextButton(onClick = onPayExisting) {
                    TextNormal(
                        stringResource(Res.string.probe_demo_exists_pay),
                        color = AppColor.StrikePrimary,
                        fontWeight = FontWeight.Bold
                    )
                }
            } else {
                TextButton(onClick = onDismiss) { TextNormal(stringResource(Res.string.ok)) }
            }
        },
        dismissButton = {
            if (demoExists) {
                TextButton(onClick = onDismiss) { TextNormal(stringResource(Res.string.cancel)) }
            }
        }
    )
}

/** Maps any exception of a probe route to a [ProbeCallError] (never throws, cancellation excepted). */
fun Throwable.toProbeCallError(call: ProbeCall = ProbeCall.Create): ProbeCallError {
    if (this is CancellationException) throw this
    return if (this is com.nonopn.skydome.common.app.api.BackendErrorException) {
        ProbeCallError(classifyProbeFailure(code.value, body, call))
    } else {
        ProbeCallError(ProbeFailure.Generic)
    }
}
